import { Injectable, OnDestroy } from '@angular/core';
import { Store } from '@ngrx/store';
import { BehaviorSubject, ReplaySubject } from 'rxjs';
import { takeUntil, pairwise, distinctUntilChanged } from 'rxjs/operators';

import { LaunchDarklyFlags } from 'app/core/services/launch-darkly/launch-darkly.types';
import { selectors as coreSelectors, getFeatureFlagObservable } from 'app/core/state';
import assertDefined from 'app/utils/assert-defined';
import { Loggable } from 'app/utils/logging/loggable';
import User from 'entity/User';
import { BaseUser, Presentation, UpdateType } from 'messages/websocket.messages';

import SocialStateMachine from '../social-state-machine/social-state-machine';
import { socialAppSelectors } from '../state/app';

import { HeaderToastMessageService } from './header-toast-message.service';
import { SceneService } from './scene.service';
import { TransitionPresentationService } from './transition-presentation.service';
import { LoadingTransitionLabel, TransitionService } from './transition.service';

@Injectable()
export class PerformerService extends Loggable implements OnDestroy {
  private isInProgress = false;
  private updatePerformerNameSource = new BehaviorSubject<boolean>(false);
  private user?: User;
  private destroyed$ = new ReplaySubject<boolean>(1);
  private speakingBannerId?: number;
  private isHost = false;
  private isCrossRoom = false;

  private isUserPresenting = false;
  private isPresentationActive = false;
  private isSinglePushToTalkEnabled = false;
  private isQAEnabled = false;

  private speakingParticipants: BaseUser[] = [];

  public isPerforming$ = new BehaviorSubject<boolean>(false);
  public updatePerformerName = this.updatePerformerNameSource.asObservable();
  public lastDismissedSpeaker = '';

  //Flags
  public presentationV3 = false;

  public constructor(
    private headerToastMessageService: HeaderToastMessageService,
    private store: Store,
    private transitionService: TransitionService,
    private transitionPresentationService: TransitionPresentationService,
    private sceneService: SceneService,
  ) {
    super();
    this.setDisplayName('PerformerService');
    this.store
      .select(coreSelectors.getUserEntity)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((user) => (this.user = user));

    this.store
      .select(socialAppSelectors.isHost)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((isHost) => (this.isHost = isHost));

    this.store
      .select(socialAppSelectors.isCrossRoomPresentation)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((isCrossRoom) => (this.isCrossRoom = isCrossRoom));

    this.store
      .select(socialAppSelectors.isUserPresenting)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((isUserPresenting) => (this.isUserPresenting = isUserPresenting));

    this.store
      .select(socialAppSelectors.isPresentationActive)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((isPresentationActive) => (this.isPresentationActive = isPresentationActive));

    this.store
      .select(socialAppSelectors.isSinglePushToTalkActive)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((active) => {
        this.isSinglePushToTalkEnabled = active;
      });

    this.store
      .select(socialAppSelectors.isQueuedQAndAActive)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((active) => {
        this.isQAEnabled = active;
      });

    this.store
      .select(socialAppSelectors.getSpeakingParticipantValues)
      .pipe(distinctUntilChanged(), takeUntil(this.destroyed$))
      .subscribe((speakingParticipants) => {
        this.speakingParticipants = speakingParticipants;
      });

    getFeatureFlagObservable(this.store, LaunchDarklyFlags.presentation_v3)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((presentation_v3) => {
        this.presentationV3 = presentation_v3;
      });

    this.listenToState();
  }

  private transitionAndOpenPerformer(shouldTransition: boolean): void {
    this.isPerforming$.next(true);
    this.isInProgress = true;

    // If the last dismissed speaker is the current user, don't show the transition
    if (this.lastDismissedSpeaker === this.user?.userId) {
      this.lastDismissedSpeaker = '';
      return;
    }
    // Enters dark mode and enable loading overlay
    if (shouldTransition) {
      if (this.presentationV3) {
        void this.transitionPresentationService.executeTransitionToPresentation(LoadingTransitionLabel.AUDIENCE);
      } else {
        void this.transitionService.enableDarkModeEffect(LoadingTransitionLabel.AUDIENCE, true, true);
      }
    }
  }

  private listenToState(): void {
    this.store
      .select(socialAppSelectors.getPresentation)
      .pipe(pairwise(), takeUntil(this.destroyed$))
      .subscribe(([previousState, state]) => {
        this.indicateSpeakers();

        this.updateStateIfLoaded(previousState, state, true);
      });
  }

  private updateStateIfLoaded(
    previousState: Presentation | undefined,
    state: Presentation | undefined,
    shouldTransition = true,
  ): void {
    if (this.sceneService.isLoading) {
      setTimeout(() => this.updateStateIfLoaded(previousState, state, false), 200);
      return;
    }
    // Presentation starting
    if (state && !this.isInProgress && this.shouldShowPresentation()) {
      this.transitionAndOpenPerformer(shouldTransition);
    }
    // Presentation ending
    if (previousState && !state && this.isInProgress) {
      // Returns background to full opacity
      if (this.presentationV3) {
        this.transitionPresentationService
          .enableFadeInBackground()
          .then(() => {
            return;
          })
          .catch((e) => {
            this.error('fade in background failed', e);
            return;
          });
      } else {
        this.transitionService
          .enableFadeInBackground()
          .then(() => {
            return;
          })
          .catch((e) => {
            this.error('fade in background failed', e);
            return;
          });
      }

      this.isPerforming$.next(false);
      this.isInProgress = false;
    }

    // User went from participant to presenter
    if (this.wentFromParticipantToPresenter()) {
      return;
    }
  }

  public ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  private wentFromParticipantToPresenter(): boolean {
    assertDefined(this.user?.userId);
    return this.isPresentationActive && this.isUserPresenting && this.isInProgress;
  }

  private shouldShowPresentation(): boolean {
    assertDefined(this.user?.userId);
    return this.isPresentationActive && !this.isUserPresenting && !this.isInProgress;
  }

  /**
   * Indicate to users who is speaking in PTT-one and Q&A mode based on social state.
   */
  private indicateSpeakers() {
    assertDefined(this.user?.userId);
    if (!this.isPresentationActive) {
      this.removeSpeakingBanner();
      return;
    }

    const { userId: currentUserId } = this.user;
    const speakingParticipant = this.speakingParticipants[0];
    const singleSpeakerMode = this.isSinglePushToTalkEnabled || this.isQAEnabled;

    if (singleSpeakerMode && speakingParticipant) {
      let message = `${speakingParticipant.firstName ?? ''} ${speakingParticipant.lastName ?? ''} is talking`;

      // Speakers and hosts are allowed to dismiss the current speaker.
      const isHostOrPresenter = this.isUserPresenting || this.isHost;

      const isSelfSpeaking = speakingParticipant.userId === currentUserId;

      if (isSelfSpeaking) {
        message = 'You are speaking to the room';
      }

      const localUser = this.user;

      this.speakingBannerId = this.headerToastMessageService.pushStaticMessage(
        {
          message,
          buttonText: isHostOrPresenter || isSelfSpeaking ? 'END TURN' : undefined,
          replace: this.speakingBannerId,
        },
        (id) => {
          this.headerToastMessageService.removeMessage(id);
          this.handleDismissSpeaker({ speakingParticipant, localUser });
          this.speakingBannerId = undefined;
        },
      );
      return;
    }
    // Remove the speaker notification when there isn't one.
    this.removeSpeakingBanner();
  }

  private removeSpeakingBanner(): void {
    this.headerToastMessageService.removeMessage(this.speakingBannerId);
    this.speakingBannerId = undefined;
  }

  /**
   * Callback called when a speaker should be dismissed based on user action
   * @param {Object} obj - Parameter object
   * @param {User} obj.localUser - The current local user. This may be called at log out, so the user member may not be used.
   * @param {User} obj.speakingParticipant - The speaking participant to dismiss
   */
  private handleDismissSpeaker({
    localUser,
    speakingParticipant,
  }: {
    speakingParticipant: User;
    localUser: User;
  }): void {
    const { userId } = localUser;
    assertDefined(speakingParticipant.userId);
    assertDefined(userId);

    if (!this.isPresentationActive) {
      this.warn('Cannot remove speaking participant, presentation is not active');
      return;
    }

    if (this.speakingParticipants?.[Number(userId)]) {
      this.warn('Tried to dismiss non-speaking participant', { speakingParticipant });
      return;
    }

    this.info('Dismissed speaker', speakingParticipant);
    this.lastDismissedSpeaker = speakingParticipant.userId;
    SocialStateMachine.shared
      .requestRemoveSpeakingParticipant({
        participantId: speakingParticipant.userId,
        userId: userId,
        updateType: this.isCrossRoom ? UpdateType.ALL : UpdateType.CURRENT,
      })
      .catch((error) => {
        this.error('Failed to remove speaking participant', error);
      });
  }
}
